import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PLATFORM_DP_ENVIRONMENT } from '@jfw/angular/shared/tokens';
import { AsPicturedItem, ValidStyle } from '@jfw/types';
import { catchError, map, Observable, of, retry, tap } from 'rxjs';

// A genuinely empty result array already means "the backend answered: no
// matches" to getValidStylesByStyleCode's own consumers (WholesaleStyleInputComponent's
// validateStyle() etc.) -- this sentinel lets a caller tell that apart from
// "the request itself never got a real answer" (retries exhausted), same
// idiom as WholesaleInventoryService's INVENTORY_CHECK_ERRORED. See EXT-6062.
export const STYLE_LOOKUP_ERRORED = Symbol('style-lookup-errored');
export type StyleLookupErrored = typeof STYLE_LOOKUP_ERRORED;

@Injectable({
  providedIn: 'root',
})
export class WholesaleStyleService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(PLATFORM_DP_ENVIRONMENT);
  private readonly styleApiUrl = this.environment.style_rest_api_server_url;

  private readonly reqHeader = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  // Style catalog data is global reference data, not account-scoped -- safe
  // to persist across tabs/sessions in localStorage rather than sessionStorage,
  // with a much longer TTL than the per-query cache this replaced.
  private readonly CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 1 day
  private readonly CACHE_PREFIX = 'style-autocomplete-cache';

  public getValidStylesByStyleCode(
    prodId: string,
    styleCode: string,
  ): Observable<ValidStyle[] | StyleLookupErrored> {
    const normalizedStyleCode = styleCode.toLowerCase();

    // Cache is keyed by individual (prodId, style) pairs actually seen in a
    // real backend response, built up incrementally across every query --
    // never by the query string itself (EXT-5593). A query is only ever
    // served from cache when it's ITSELF already a known exact style code:
    // that record was returned directly by the backend at some point and
    // can't be an incomplete subset. A partial prefix being typed, or any
    // style not yet seen, always goes to the network fresh rather than
    // being derived/filtered from whatever's cached so far -- that
    // derivation step is exactly what silently dropped real matches
    // (e.g. a cached "b" query's own capped page hiding a real "bxbl")
    // under the old per-query cache design.
    const cache = this.readCache(prodId);
    if (cache && normalizedStyleCode in cache.styles) {
      return of([cache.styles[normalizedStyleCode]]);
    }

    const validStyleUrl = `${this.styleApiUrl}/valid-styles/${prodId}/${styleCode}`;

    return this.http
      .get<ValidStyle[]>(validStyleUrl, { headers: this.reqHeader })
      .pipe(
        map((results) => {
          const seenStyles = new Set<string>();
          return results.filter((item) => {
            if (seenStyles.has(item.style)) {
              return false;
            }
            seenStyles.add(item.style);
            return true;
          });
        }),
        tap((filteredResults) => {
          this.mergeIntoCache(prodId, filteredResults);
        }),
        // EXT-6062: same idiom as inventory.service.ts's retry-then-sentinel
        // (EXT-6061) -- a single transient failure must not permanently latch
        // validateStyle()'s consumer at "styleNotFound", indistinguishable
        // from a genuine no-match answer.
        retry({ count: 2, delay: 1000 }),
        catchError((error) => {
          console.error('Failed to fetch valid styles', error);
          return of(STYLE_LOOKUP_ERRORED);
        }),
      );
  }

  // EXT-6524: errors propagate so Order Entry can tell "no match" (an empty
  // or coat-only list) from a failed request.
  public getAsPictured(styleCode: string): Observable<AsPicturedItem[]> {
    const url = `${this.styleApiUrl}/as-pictured/${styleCode}`;
    return this.http.get<AsPicturedItem[]>(url, { headers: this.reqHeader });
  }

  private getCacheKey(prodId: string): string {
    return `${this.CACHE_PREFIX}-${prodId}`;
  }

  private readCache(
    prodId: string,
  ): { timestamp: number; styles: Record<string, ValidStyle> } | null {
    // Merely accessing localStorage (not just parsing what it returns) can
    // throw a SecurityError in browsers/settings that block all site storage
    // alongside cookies (e.g. Safari with cookies fully disabled) -- the
    // getItem() call itself must be inside this try, not just the JSON
    // parsing below, or a user with storage disabled gets a hard crash on
    // every style lookup instead of a graceful fall-through to the network.
    try {
      const cachedEntry = localStorage.getItem(this.getCacheKey(prodId));
      if (!cachedEntry) return null;

      const parsed = JSON.parse(cachedEntry);
      const isValidCache =
        parsed.site === this.environment.site &&
        parsed.envVersion === this.environment.envVersion &&
        Date.now() - parsed.timestamp < this.CACHE_DURATION_MS;
      return isValidCache ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  private mergeIntoCache(prodId: string, newStyles: ValidStyle[]): void {
    try {
      const existing = this.readCache(prodId);
      const styles = existing ? { ...existing.styles } : {};
      for (const style of newStyles) {
        styles[style.style.toLowerCase()] = style;
      }
      const cacheData = {
        timestamp: Date.now(),
        styles,
        site: this.environment.site,
        envVersion: this.environment.envVersion,
      };
      localStorage.setItem(
        this.getCacheKey(prodId),
        JSON.stringify(cacheData),
      );
    } catch (e) {
      // Silently handle storage full errors
      console.warn('Local storage is full, unable to cache styles.');
    }
  }
}
